/**
 * #1150 — the Novig manual ticket for one locked outcome.
 *
 * Presentation only: terms are native (probability text, whole $0.01-payout
 * contracts, exact USD cost/payout) and submit stays disabled with every
 * blocking reason listed at the action until an execution adapter exists.
 * The draft belongs to one subaccount + outcome: changing either remounts it,
 * and feed/clock updates never write to it — only an explicit level click
 * (a new `seededPrice.key`) sets the price.
 */
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/infra/utils';
import {
  NOVIG_SUBMIT_NOT_CONNECTED,
  novigDraftKey,
  presentNovigTicketAction,
  type NovigAccountReadiness,
  type NovigFeedReadiness,
  type NovigOutcomeQuote,
} from '@/lib/novig/presenter';

export interface NovigSeededPrice {
  /** Unique per click; the ticket applies each key once. */
  readonly key: string;
  /** `novigDraftKey` the click was made for: a seed never crosses into
   * another subaccount's or outcome's draft. */
  readonly draftKey: string;
  readonly probability: string;
}

export interface NovigManualTicketProps {
  target: NovigOutcomeQuote;
  feed: NovigFeedReadiness;
  account: NovigAccountReadiness;
  nowMs: number;
  seededPrice: NovigSeededPrice | null;
}

export function NovigManualTicket(props: NovigManualTicketProps) {
  const draftKey = novigDraftKey(props.account, props.target);
  return <TicketBody key={draftKey} draftKey={draftKey} {...props} />;
}

const input = 'bg-muted w-full rounded-md px-2 py-1.5 text-sm tabular-nums outline-none focus:ring-1 focus:ring-primary';

function TicketBody({ draftKey, target, feed, account, nowMs, seededPrice }: NovigManualTicketProps & { draftKey: string }) {
  const [priceText, setPriceText] = useState('');
  const [qtyText, setQtyText] = useState('');
  // A seed for this draft present at mount is the click that opened it; later
  // seeds apply once each. Feed or clock rerenders carry the same key and are ignored.
  const appliedSeed = useRef<string | null>(null);
  useEffect(() => {
    if (!seededPrice || seededPrice.draftKey !== draftKey || seededPrice.key === appliedSeed.current) return;
    appliedSeed.current = seededPrice.key;
    setPriceText(seededPrice.probability);
  }, [seededPrice, draftKey]);

  const view = presentNovigTicketAction({ feed, account, priceText, qtyText, nowMs });
  const amounts = view.amounts;
  return (
    <div className="space-y-2 p-3" data-testid="novig-ticket">
      <div className="min-w-0">
        <div className="text-muted-foreground text-[11px] font-medium">Buy outcome</div>
        <div className="truncate text-sm font-semibold" title={target.label} data-testid="novig-ticket-outcome">
          {target.label}
        </div>
        <div className="text-muted-foreground text-[10px]">Moneyline · Full game</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="min-w-0">
          <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Price (probability)</span>
          <input className={input} inputMode="decimal" placeholder="0.000" value={priceText}
            aria-label="Novig price as probability" data-testid="novig-ticket-price"
            onChange={(e) => setPriceText(e.target.value.trim())} />
        </label>
        <label className="min-w-0">
          <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Quantity (contracts, $0.01 each)</span>
          <input className={input} inputMode="numeric" placeholder="0" value={qtyText}
            aria-label="Novig quantity in contracts" data-testid="novig-ticket-qty"
            onChange={(e) => setQtyText(e.target.value.trim())} />
        </label>
      </div>
      {/* Always rendered so terms becoming valid never shift the action below. */}
      <div className="flex h-8 justify-between gap-2 overflow-y-auto text-xs tabular-nums" data-testid="novig-ticket-amounts">
        <span>Cost <span className="font-semibold">{amounts ? `$${amounts.purchaseCostUsd}` : '—'}</span></span>
        <span>Pays <span className="font-semibold">{amounts ? `$${amounts.winningPayoutUsd}` : '—'}</span> if it wins</span>
      </div>
      <button type="button" disabled aria-describedby="novig-ticket-reasons" data-testid="novig-ticket-submit"
        className="bg-muted text-muted-foreground w-full cursor-not-allowed rounded-md py-2 text-xs font-bold uppercase tracking-wider">
        {NOVIG_SUBMIT_NOT_CONNECTED}
      </button>
      <ul id="novig-ticket-reasons" data-testid="novig-ticket-reasons"
        className={cn('h-16 overflow-y-auto text-[11px] leading-4 text-amber-500 light:text-amber-700')}>
        {view.reasons.map((r) => <li key={r} className="break-words [overflow-wrap:anywhere]">{r}</li>)}
      </ul>
    </div>
  );
}
